import React from 'react';
import {
  Box,
  Typography,
  Stack,
  alpha,
  Card,
  CardContent,
} from '@mui/material';
import {
  TrendingDown,
  Analytics,
  Timeline,
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  LineChart,
  Line,
  ReferenceLine,
  ReferenceArea,
} from 'recharts';

// Custom Tooltip Component
const CustomTooltip = ({ active, payload, label }) => {
  const theme = useTheme();
  if (active && payload && payload.length) {
    return (
      <Box
        sx={{
          bgcolor: theme.palette.background.paper,
          border: `1px solid ${alpha(theme.palette.divider, 0.2)}`,
          borderRadius: 2,
          p: 1.5,
          boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.15)}`,
        }}
      >
        <Typography variant="caption" fontWeight={600} sx={{ mb: 1, display: 'block' }}>
          {label}
        </Typography>
        {payload.map((entry, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              mb: 0.5,
            }}
          >
            <Box
              sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: entry.color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {entry.name}:
            </Typography>
            <Typography variant="caption" fontWeight={700} sx={{ color: entry.color }}>
              {typeof entry.value === 'number' ? entry.value.toFixed(1) : entry.value}%
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
  return null;
};

const ChartsSection = ({ chartData }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  
  if (!chartData || chartData.length === 0) {
    return null;
  }

  return (
    <Stack spacing={3}>
        {/* Risk Score Timeline - Main Chart */}
        <Card
          elevation={0}
          sx={{
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
            borderRadius: 3,
            background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            {/* Header */}
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 3 }}>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.3)}`,
                }}
              >
                <Timeline sx={{ color: 'white', fontSize: 28 }} />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Typography variant="h5" fontWeight={700} sx={{ letterSpacing: -0.5, mb: 0.5 }}>
                  {t('downtrend.charts.riskScoreTimeline')}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('downtrend.charts.riskAnalysisDesc', 'Risk analysis across 10, 20 and 60-day timeframes')}
                </Typography>
              </Box>
            </Stack>

            {/* Chart */}
            <Box sx={{ width: '100%', height: 450, mt: 2 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={chartData}
                  margin={{ top: 20, right: 30, left: 20, bottom: 20 }}
                >
                  <defs>
                    <linearGradient id="colorRisk10" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorRisk20" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#f59e0b" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="colorRisk60" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ef4444" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke={alpha(theme.palette.divider, 0.2)}
                    vertical={false}
                  />
                  <XAxis
                    dataKey="date"
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    tickLine={false}
                    axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                  />
                  <YAxis
                    stroke={theme.palette.text.secondary}
                    style={{ fontSize: '0.75rem' }}
                    domain={[0, 100]}
                    tickLine={false}
                    axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    tickFormatter={(value) => `${value}%`}
                  />
                  <RechartsTooltip content={<CustomTooltip />} />
                  <Legend
                    wrapperStyle={{ fontSize: '0.875rem', paddingTop: '20px' }}
                    iconType="circle"
                  />
                  
                  {/* Reference Lines for Risk Thresholds */}
                  <ReferenceLine y={30} stroke="#16a34a" strokeDasharray="5 5" strokeWidth={1.5} label={{ value: t('downtrend.charts.low', 'Low'), position: "right", fill: "#16a34a", fontSize: 11 }} />
                  <ReferenceLine y={50} stroke="#f59e0b" strokeDasharray="5 5" strokeWidth={1.5} label={{ value: t('downtrend.charts.medium', 'Medium'), position: "right", fill: "#f59e0b", fontSize: 11 }} />
                  <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1.5} label={{ value: t('downtrend.charts.high', 'High'), position: "right", fill: "#ef4444", fontSize: 11 }} />
                  
                  {/* Risk Areas */}
                  <ReferenceArea y1={0} y2={30} fill="#16a34a" fillOpacity={0.05} />
                  <ReferenceArea y1={30} y2={50} fill="#f59e0b" fillOpacity={0.05} />
                  <ReferenceArea y1={50} y2={70} fill="#f59e0b" fillOpacity={0.08} />
                  <ReferenceArea y1={70} y2={100} fill="#ef4444" fillOpacity={0.08} />
                  
                  <Area
                    type="monotone"
                    dataKey="risk10"
                    stroke="#3b82f6"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorRisk10)"
                    name={t('downtrend.charts.risk10d')}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="risk20"
                    stroke="#f59e0b"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorRisk20)"
                    name={t('downtrend.charts.risk20d')}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                  <Area
                    type="monotone"
                    dataKey="risk60"
                    stroke="#ef4444"
                    strokeWidth={2.5}
                    fillOpacity={1}
                    fill="url(#colorRisk60)"
                    name={t('downtrend.charts.risk60d')}
                    dot={false}
                    activeDot={{ r: 5 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Box>
          </CardContent>
        </Card>

        {/* Tech vs Macro Risk - Side by Side */}
        <Box
          sx={{
            display: 'grid',
            gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' },
            gap: 3,
            width: '100%',
          }}
        >
          {/* Technical Risk Chart */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 3,
              background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${alpha('#3b82f6', 0.3)}`,
                  }}
                >
                  <TrendingDown sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                    {t('downtrend.charts.technicalRisk')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('downtrend.charts.technicalRiskDesc', 'Risk from technical analysis')}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ width: '100%', height: 320, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <defs>
                      <linearGradient id="colorTechRisk" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#3b82f6" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#3b82f6" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={alpha(theme.palette.divider, 0.2)}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.7rem' }}
                      tickLine={false}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.7rem' }}
                      domain={[0, 100]}
                      tickLine={false}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1} label={{ value: t('downtrend.charts.high', 'High'), position: "right", fill: "#ef4444", fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="techRisk"
                      stroke="#3b82f6"
                      strokeWidth={3}
                      name={t('downtrend.charts.technicalRisk')}
                      dot={false}
                      activeDot={{ r: 6, fill: '#3b82f6' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>

          {/* Macro Risk Chart */}
          <Card
            elevation={0}
            sx={{
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
              borderRadius: 3,
              background: `linear-gradient(to bottom, ${alpha(theme.palette.background.paper, 1)} 0%, ${alpha(theme.palette.grey[50], 0.3)} 100%)`,
              overflow: 'visible',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: 1.5,
                    background: `linear-gradient(135deg, #f59e0b 0%, #d97706 100%)`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    boxShadow: `0 2px 8px ${alpha('#f59e0b', 0.3)}`,
                  }}
                >
                  <Analytics sx={{ color: 'white', fontSize: 20 }} />
                </Box>
                <Box>
                  <Typography variant="h6" fontWeight={700} sx={{ letterSpacing: -0.5 }}>
                    {t('downtrend.charts.macroRisk')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {t('downtrend.charts.macroRiskDesc', 'Risk from macroeconomic environment')}
                  </Typography>
                </Box>
              </Stack>
              <Box sx={{ width: '100%', height: 320, mt: 2 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData} margin={{ top: 10, right: 20, left: 10, bottom: 10 }}>
                    <defs>
                      <linearGradient id="colorMacroRisk" x1="0" y1="0" x2="1" y2="0">
                        <stop offset="0%" stopColor="#f59e0b" stopOpacity={0.8} />
                        <stop offset="100%" stopColor="#f59e0b" stopOpacity={0.2} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid
                      strokeDasharray="3 3"
                      stroke={alpha(theme.palette.divider, 0.2)}
                      vertical={false}
                    />
                    <XAxis
                      dataKey="date"
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.7rem' }}
                      tickLine={false}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                    />
                    <YAxis
                      stroke={theme.palette.text.secondary}
                      style={{ fontSize: '0.7rem' }}
                      domain={[0, 100]}
                      tickLine={false}
                      axisLine={{ stroke: alpha(theme.palette.divider, 0.3) }}
                      tickFormatter={(value) => `${value}%`}
                    />
                    <RechartsTooltip content={<CustomTooltip />} />
                    <ReferenceLine y={70} stroke="#ef4444" strokeDasharray="5 5" strokeWidth={1} label={{ value: t('downtrend.charts.high', 'High'), position: "right", fill: "#ef4444", fontSize: 10 }} />
                    <Line
                      type="monotone"
                      dataKey="macroRisk"
                      stroke="#f59e0b"
                      strokeWidth={3}
                      name={t('downtrend.charts.macroRisk')}
                      dot={false}
                      activeDot={{ r: 6, fill: '#f59e0b' }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              </Box>
            </CardContent>
          </Card>
        </Box>
      </Stack>
  );
};

export default ChartsSection;

